import { Button } from '@amore/ui/components/button'
import { StateView } from '@amore/ui/components/state-view'
import { useNavigate, useParams, useRouterState, useSearch } from '@tanstack/react-router'
import { MapPin, Plus } from 'lucide-react'
import placeIllustration from '@/assets/planner/place.svg'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { venueIdentity } from '@/mock/venues'
import { EmptyState } from '@/patterns/empty-state'
import { PromptList } from '@/patterns/prompts'
import { Screen } from '@/patterns/screen'
import { SectionTabs } from '@/patterns/section-tabs'
import { AddPlaceSheet } from './add-place-sheet'
import { type VenueEditSection, venueEditSections, venuePrompts } from './model'
import { PlacePlan } from './place-plan'
import { PlaceSheet } from './place-sheet'
import { VenueBudgetNote, VenueTasks } from './preparations'

export function VenuesScreen() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const navigate = useNavigate()
  const { venueId } = useParams({ strict: false })
  const search = useSearch({ strict: false })
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const adding = pathname === '/venues/new'
  const selectedId = adding && typeof search.place === 'string' ? search.place : venueId
  const opened = selectedId ? data.venues.find((venue) => venue.id === selectedId) : data.venues[0]
  const editing = venueEditSections.find((section) => section === search.edit)
  const openPlace = (id: string, edit?: VenueEditSection) =>
    void navigate({ to: `/venues/${id}`, search: { edit }, resetScroll: false })
  const close = () => {
    void navigate({
      to: opened ? `/venues/${opened.id}` : '/venues',
      search: {},
      resetScroll: false,
    }).then(() =>
      requestAnimationFrame(() =>
        document.getElementById('add-venue')?.focus({ preventScroll: true }),
      ),
    )
  }
  const add = () =>
    void navigate({
      to: '/venues/new',
      search: { place: opened?.id },
      resetScroll: false,
    })
  const missing = pathname.startsWith('/venues/') && !adding && !opened
  return (
    <Screen
      eyebrow="Plánovač"
      title="Místa"
      illustration={<img src={placeIllustration} alt="" className="h-auto w-full" />}
      description="Každé místo a všechno, co kolem něj potřebujete domluvit."
      tabs={
        data.venues.length ? (
          <SectionTabs
            label="Místa svatby"
            active={opened ? `/venues/${opened.id}` : ''}
            tabs={data.venues.map((venue) => ({
              to: `/venues/${venue.id}`,
              label: venueIdentity(venue, data).name,
            }))}
            action={
              canEdit ? (
                <Button id="add-venue" appearance="ghost" tone="neutral" size="sm" onClick={add}>
                  <Plus aria-hidden="true" className="size-3.5" />
                  Přidat místo
                </Button>
              ) : undefined
            }
          />
        ) : undefined
      }
    >
      {missing ? (
        <StateView
          state="unavailable"
          placement="panel"
          heading="Tohle místo už v plánu není"
          actions={
            <Button onClick={() => void navigate({ to: '/venues', search: {} })}>
              Zpět na místa
            </Button>
          }
        />
      ) : null}
      {data.venues.length === 0 ? (
        <EmptyState
          icon={<MapPin aria-hidden="true" />}
          title={canEdit ? 'Kde se bude vaše svatba odehrávat?' : 'Zatím tu není žádné místo'}
          description={
            canEdit
              ? 'Zámek, oblíbená restaurace nebo zahrada u rodičů. Začněte jedním místem, další přidáte kdykoli.'
              : 'Až správce přidá místa této svatby, uvidíte je tady.'
          }
          actions={
            canEdit ? (
              <Button id="add-venue" onClick={add}>
                <Plus aria-hidden="true" className="size-4" /> Přidat první místo
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {opened ? (
        <PlacePlan
          venue={opened}
          onEdit={(edit) =>
            void navigate({ to: pathname, search: { ...search, edit }, resetScroll: false })
          }
        />
      ) : null}
      {canEdit ? (
        <PromptList
          title="Co ještě chybí"
          prompts={venuePrompts(data)
            .filter((prompt) => prompt.venueId === opened?.id)
            .map((prompt) => ({
              ...prompt,
              action: (
                <Button
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() =>
                    openPlace(
                      prompt.venueId,
                      prompt.panel === 'operations' ? 'operations' : 'agreement',
                    )
                  }
                >
                  Doplnit
                </Button>
              ),
            }))}
          onHide={(code) =>
            update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
          }
        />
      ) : null}
      {opened ? (
        <>
          <VenueTasks name={venueIdentity(opened, data).name} />
          <VenueBudgetNote />
        </>
      ) : null}
      {opened && editing ? (
        <PlaceSheet
          key={`${opened.id}-${editing}`}
          venue={opened}
          section={editing}
          onClose={() => {
            void navigate({
              to: pathname,
              search: { ...search, edit: undefined },
              resetScroll: false,
            }).then(() =>
              requestAnimationFrame(() =>
                document
                  .getElementById(`edit-venue-${opened.id}-${editing}`)
                  ?.focus({ preventScroll: true }),
              ),
            )
          }}
          onRemoved={() => void navigate({ to: '/venues', search: {}, resetScroll: false })}
        />
      ) : null}
      {adding && canEdit ? (
        <AddPlaceSheet onClose={close} onCreated={(id) => openPlace(id)} />
      ) : null}
    </Screen>
  )
}
